import React, { useState, useEffect } from 'react';
import { Button, Paper, TextField, Typography, FormControl, InputLabel, Select, MenuItem, Box, Divider, Checkbox, ListItemText, OutlinedInput } from '@mui/material';
import { Link } from 'react-router-dom';
import ApplicationRow from '../components/application/row/row';
import { ApplicationRowExDTO } from '../DTO/ApplicationRowExDTO';
import { APIService } from '../services/apiService';
import { HOME_SCHOOL_ID, ROUTES } from '../constants';
import { useSchools } from '../contexts';
import { VerdictDTO } from '../DTO/VerdictDTO';
import { MasterTrackDTO } from '../DTO/MasterTrackDTO';
import {
	ApplicationContext,
	ApplicationUpdateContext,
} from '../components/application';
import { ApplicationState } from '../components/application/ApplicationState';
import { MockApplicationHandlers } from '../components/application/ApplicationHandlers';

const api = new APIService();
const privileges = {
	addCourses: false,
	editRows: false,
	editCourses: false,
	addComment: true,
	giveVerdict: true,
};

const sideMargin = { marginLeft: '10px', marginRight: '10px' };

// Custom hook for localStorage persistence
function useLocalStorageState<T>(key: string, defaultValue: T) {
	const [state, setState] = useState<T>(() => {
		try {
			const item = window.localStorage.getItem(key);
			return item ? JSON.parse(item) : defaultValue;
		} catch {
			return defaultValue;
		}
	});

	const setValue = (value: T) => {
		try {
			setState(value);
			window.localStorage.setItem(key, JSON.stringify(value));
		} catch (error) {
			console.error(`Error saving to localStorage:`, error);
		}
	};

	return [state, setValue] as const;
}

const getAvailableYears = (): number[] => {
	const currentYear = new Date().getFullYear();
	const years = [];
	// 2021 is the year of the earliest applications
	// Allow filtering to next year as students apply for the next year's semester
	for (let year = 2021; year <= currentYear + 1; year++) {
		years.push(year);
	}
	return years.reverse(); // Most recent first
};

const ApplicationRowsAdmin = (): JSX.Element => {
	const schools = useSchools();
	const [applicationRows, setApplicationRows] = useState<
		ApplicationRowExDTO[] | null
	>(null);
	const [masterTracks, setMasterTracks] = useState<MasterTrackDTO[]>([]);
	
	const [selectedMasterTrackIds, setSelectedMasterTrackIds] = useLocalStorageState<number[]>(
		'applicationRows_selectedMasterTrackIds',
		[]
	);
	const [selectedYear, setSelectedYear] = useState<string>('');

	const loadMasterTracks = async () => {
		try {
			const masterTracksData = await api.getMasterTracks();
			setMasterTracks(masterTracksData);
		} catch (error) {
			console.error('Failed to load master tracks:', error);
		}
	};

	const getRows = (masterTrackIds?: number[], year?: number) => {
		api.getUnhandledApplicationRows(masterTrackIds, year).then((data) => {
			setApplicationRows(data);
		});
	}

	useEffect(() => {
		loadMasterTracks();
	}, []);

	useEffect(() => {
		getRows(
			selectedMasterTrackIds.length > 0 ? selectedMasterTrackIds : undefined,
			selectedYear ? Number(selectedYear) : undefined
		);
	}, [selectedMasterTrackIds, selectedYear]);

	// TODO: Den här gick nog sönder i application refaktorering.
	const getVerdictMappingFunction = (idx: number, verdict: VerdictDTO) => {
		if (!applicationRows) return;
		applicationRows[idx].activeVerdict = verdict;
		setApplicationRows([...applicationRows]);
	};

	const clearFilters = () => {
		setSelectedMasterTrackIds([]);
		setSelectedYear('');
	};

	if (!applicationRows) return <p>Loading rows...</p>;
	return (
		<ApplicationContext.Provider
			value={new ApplicationState({ privileges })}
		>
			<ApplicationUpdateContext.Provider
				value={
					new MockApplicationHandlers({
						giveVerdict: getVerdictMappingFunction,
					})
				}
			>
				<Typography
					gutterBottom
					style={{ marginTop: '5px' }}
					variant="h4"
					align="center"
				>
					Application rows
				</Typography>
				
				{/* Filters */}
				<Box sx={{ marginBottom: 2, marginTop: 1, display: 'flex', gap: 2 }}>
				<FormControl sx={{ width: 300 }}>
					<InputLabel>Master Tracks</InputLabel>
						<Select
							multiple
							value={selectedMasterTrackIds}
							onChange={(e) => setSelectedMasterTrackIds(e.target.value as number[])}
							input={<OutlinedInput label="Master Tracks" />}
							renderValue={(selected) => 
								selected.map(id => masterTracks.find(t => t.id === id)?.name).join(', ')
							}
						>
							{masterTracks.map((track) => (
								<MenuItem key={track.id} value={track.id}>
									<Checkbox checked={selectedMasterTrackIds.includes(track.id)} />
									<ListItemText primary={track.name} />
								</MenuItem>
							))}
						</Select>
					</FormControl>					
					<FormControl sx={{ width: 200 }}>
						<InputLabel>Year</InputLabel>
						<Select
							value={selectedYear}
							label="Year"
							onChange={(e) => setSelectedYear(e.target.value)}
						>
							<MenuItem value="">All Years</MenuItem>
							{getAvailableYears().map(year => (
								<MenuItem key={year} value={year}>
									{year}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<Button 
						variant="outlined" 
						onClick={clearFilters}
						disabled={selectedMasterTrackIds.length === 0 && selectedYear === ''}
					>
						Clear Filters
					</Button>
				</Box>
				
				<Divider sx={{ marginBottom: 2 }} />
				{applicationRows.length === 0 ? (
					<p>No unhandled rows.</p>
				) : null}
				{applicationRows.map((row, idx) => (
					<Paper
						key={row.applicationRowId}
						elevation={5}
						style={{
							padding: '10px',
							marginBottom: '20px',
							marginTop: '10px',
						}}
					>
						<div
							style={{
								borderBottom: '1px solid #cccccc',
								paddingBottom: '10px',
							}}
						>
							<TextField
								type="text"
								value={row.userName}
								label="Student"
								variant="outlined"
								style={sideMargin}
								disabled={true}
							/>
							<TextField
								type="text"
								value={row.masterProgram}
								label="Master track"
								variant="outlined"
								style={sideMargin}
							/>
							<Button
								variant="contained"
								color="primary"
								size="small"
								style={{ margin: '10px' }}
								component={Link}
								to={`${ROUTES.APPLICATION}/${row.applicationHeadId}`}
							>
								Open application
							</Button>
						</div>
						<br />
						<div>
							<ApplicationRow
								row={{ ...row, id: row.applicationRowId }}
								schools={{
									home:
										schools?.find(
											(s) => s.id === HOME_SCHOOL_ID
										) || null,
									exchange:
										schools?.find(
											(s) => s.id === row.schoolId
										) || null,
								}}
								overridePrivileges={privileges}
								rowIndex={idx}
							/>
						</div>
					</Paper>
				))}
			</ApplicationUpdateContext.Provider>
		</ApplicationContext.Provider>
	);
};

export default ApplicationRowsAdmin;
